import React, { createContext, useState, useEffect, useContext } from "react";
import en from "../locales/en.json";
import pt from "../locales/pt.json";
import de from "../locales/de.json";
import tr from "../locales/tr.json";


type TermsPrivacySection = {
  title: string;
  content: string;
};

type TermsPrivacyText = {
  title: string;
  intro: string;
  sections: TermsPrivacySection[];
};

type TranslationType = {
  app_title: string;
  hero_description: string;
  signin: string;
  signup: string;
  email: string;
  password: string;
  forgot_password: string;
  contact_us: string;
  terms_conditions: string;
  privacy_policy: string;
  features: string;
  testimonials: string;
  highlights: string;
  pricing: string;
  faq: string;
  blog: string;
  logout: string;

  // ✅ Store Creation Section (NEW)
  store_creation_section: {
    upload_logo: string;
    logo: string;
    step1: string;
    step2: string;
    step3: string;
    thank_you: string;
    review_message: string;
    go_to_dashboard: string;
    previous: string;
    next: string;
    submit_request: string;
    store_name: string;
    store_name_placeholder: string;
    phone: string;
    phone_placeholder: string;
    website: string;
    website_placeholder: string;
    address1: string;
    address1_placeholder: string;
    address2: string;
    address2_placeholder: string;
    city: string;
    city_placeholder: string;
    postal_code: string;
    postal_code_placeholder: string;
    country: string;
    country_placeholder: string;
    define_offers: string;
    rewards_info: string;
    reward_name: string;
    reward_name_placeholder: string;
    points_required: string;
    points: string;
    add_offer: string;
    remove: string;
    store_contact: string;
    address: string;
    rewards: string;
    reward_points: string;

  };
  home_page_section: {
    overview: string;
    create_new_store: string;
    edit: string;
    store_action_logs: string;
    user: string;
    action_type: string;
    value: string;
    action_time: string;
    store: string;
  };
  

  store_actions_section: {
    title: string;
    total_actions: string;
    visits_per_month: string;
    visit_label: string;
    reward_label: string;
  };

  chart_section: {
    page_views_title: string;
    page_views_description: string;
    page_views_label: string;
  };
  
  action_logs_section: {
    user: string;
    action_type: string;
    value: string;
    action_time: string;
    store: string;
  };
  
  // Features Section
  features_section: {
    features_description: string;
    dashboard_title: string;
    dashboard_description: string;
    mobile_title: string;
    mobile_description: string;
    platforms_title: string;
    platforms_description: string;
  };

  // Testimonials Section
  testimonials_section: {
    description: string;
    pedro_occupation: string;
    pedro_testimonial: string;
    fatih_occupation: string;
    fatih_testimonial: string;
    anil_occupation: string;
    anil_testimonial: string;
  };

  // Highlights Section
  highlights_section: {
    title: string;
    description: string;
    adaptable_performance_title: string;
    adaptable_performance_description: string;
    built_to_last_title: string;
    built_to_last_description: string;
    great_user_experience_title: string;
    great_user_experience_description: string;
    innovative_functionality_title: string;
    innovative_functionality_description: string;
    reliable_support_title: string;
    reliable_support_description: string;
    precision_detail_title: string;
    precision_detail_description: string;
  };

  // Pricing Section
  pricing_section: {
    title: string;
    free_plan: string;
    free_plan_description: string;
    price_per_month: string;
    sign_up_text: string;
  };

  // FAQ Section
  faq_section: {
    title: string;
    support_question: string;
    support_answer: string;
  };

  contact_section: {
    title: string;
    success_message: string;
    name_placeholder: string;
    email_placeholder: string;
    subject_placeholder: string;
    message_placeholder: string;
    send_button: string;
    characters: string;
  };

  // Footer Section
  footer_section: {
    product: string;
    company: string;
    legal: string;
    about_us: string;
    terms_conditions: string;
    privacy_policy: string;
    copyright: string;
  };
  color_mode: {
    system: string;
    light: string;
    dark: string;
  };
  auth: {
    signin: string;
    signup: string;
    email: string;
    password: string;
    forgot_password: string;
    remember_me: string;
    dont_have_account: string;
    already_have_account: string;
    reset_password: string;
    send_reset_link: string;
    enter_email_reset: string;
    email_verification_title: string;
    login_google: string;
    email_verification_message: string;
    full_name: string;
    cancel: string;
    close: string;
  };
  auth_pages: {
    verify_email_title: string;
    verifying_email: string;
    email_verified_success: string;
    invalid_token: string;
    error_during_verification: string;
    go_to_sign_in: string;
    back_to_sign_up: string;

    reset_password_title: string;
    new_password: string;
    confirm_new_password: string;
    passwords_do_not_match: string;
    password_updated_successfully: string;
    go_to_home: string;

    validating_your_email: string;
    please_wait: string;
    email_verification_failed: string;
    proceed_to_next_step: string;
    sign_up_again: string;
  };


  // ✅ Add Terms & Privacy
  terms_conditions_text: TermsPrivacyText;
  privacy_policy_text: TermsPrivacyText;
};

const translations: Record<string, TranslationType> = {
  en: en as TranslationType,
  pt: pt as TranslationType,
  de: de as TranslationType,
  tr: tr as TranslationType
};

interface LanguageContextProps {
  language: string;
  setLanguage: (language: string) => void;
  translations: TranslationType;
}

const LanguageContext = createContext<LanguageContextProps | null>(null);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguage] = useState<string>(
    localStorage.getItem("language") || "en"
  );
  const [translationsData, setTranslationsData] = useState<TranslationType>(
    translations[language] || translations.en
  );

  // ✅ Add force update to trigger re-renders
  const [forceUpdate, setForceUpdate] = useState(0);

  useEffect(() => {
    if (!translations[language]) {
      setLanguage("en");
    }
    setTranslationsData(translations[language] || translations.en);
    localStorage.setItem("language", language);

    // ✅ Trigger force re-render
    setForceUpdate((prev) => prev + 1);
  }, [language]);

  return (
    <LanguageContext.Provider
      value={{ language, setLanguage, translations: translationsData }}
    >
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextProps => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};

export default LanguageContext;
export type { TranslationType };
